
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { ChevronRight, MapPin, Globe } from 'lucide-react';
import { worldCountries } from '@/data/countries/index';
import SEO from '@/components/shared/SEO';

const Directory = () => {
  const { t } = useLanguage();
  
  // Structured data for the directory
  const directoryStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: worldCountries.map((country, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Country',
        name: country.name,
        url: `https://findmytherapist.com/psychologists/${country.slug}`
      }
    }))
  };
  
  return (
    <>
      <SEO
        title="Therapist Directory by Location | Find Local Psychotherapy Services"
        description="Browse our comprehensive directory of certified psychotherapists by country and city. Find local mental health professionals for in-person or online therapy sessions."
        keywords="therapist directory, psychotherapists by location, find therapist by city, local therapy services"
        canonicalUrl="https://findmytherapist.com/directory/"
        structuredData={directoryStructuredData}
        ogType="website"
      />
      
      <div className="bg-brand-blue-50 py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-brand-gray-900 mb-4 text-center">
              {t('cityDirectory')}
            </h1>
            <p className="text-lg text-brand-gray-700 text-center mb-8 max-w-2xl mx-auto">
              {t('findPsychotherapistInAnyCity')}
            </p>
            
            <nav aria-label="Breadcrumb" className="mb-8">
              <ol className="flex justify-center text-sm">
                <li className="flex items-center">
                  <Link to="/" className="text-brand-blue-600 hover:text-brand-blue-800">Home</Link>
                  <span className="mx-2">/</span>
                </li>
                <li className="text-brand-gray-600">Directory</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {worldCountries.map((country) => (
            <div key={country.code} className="bg-white rounded-xl shadow-sm border border-brand-gray-200 overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <Globe className="h-5 w-5 text-brand-blue-500 mr-2" />
                  <h2 className="text-xl font-semibold text-brand-gray-900">{country.name}</h2>
                </div>
                
                <ul className="space-y-2">
                  {country.topCities.slice(0, 5).map((city) => (
                    <li key={city.slug} className="flex items-center">
                      <MapPin className="h-4 w-4 text-brand-gray-400 mr-2" />
                      <Link 
                        to={`/city/${city.slug}`}
                        className="text-brand-gray-700 hover:text-brand-blue-600 transition-colors"
                        aria-label={`Find therapists in ${city.name}, ${country.name}`}
                      >
                        {city.name}
                      </Link>
                    </li>
                  ))}
                </ul>
                
                {country.topCities.length > 5 && (
                  <Link 
                    to={`/psychologists/${country.slug}`}
                    className="flex items-center text-brand-blue-500 hover:text-brand-blue-700 mt-4 text-sm font-medium"
                    aria-label={`See all cities in ${country.name}`}
                  >
                    {t('seeAllCities')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-12 text-center">
          <h2 className="text-2xl font-semibold text-brand-gray-900 mb-6">Related Pages</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/specialties" 
              className="py-2 px-4 bg-brand-gray-100 hover:bg-brand-gray-200 rounded-lg text-brand-gray-800 transition-colors"
            >
              Browse by Specialty
            </Link>
            <Link 
              to="/how-it-works" 
              className="py-2 px-4 bg-brand-gray-100 hover:bg-brand-gray-200 rounded-lg text-brand-gray-800 transition-colors"
            >
              How Our Service Works
            </Link>
            <Link 
              to="/for-therapists" 
              className="py-2 px-4 bg-brand-gray-100 hover:bg-brand-gray-200 rounded-lg text-brand-gray-800 transition-colors"
            >
              For Therapists
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Directory;
